import { ListFilterPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ResetButton } from "@/components/shared/resetButton";

export const useFilterV2 = () => {
  const [isOpen, setIsOpen] = useState(false);
  return { isOpen, toggle: () => setIsOpen((o) => !o) };
};

export type FilterV2Props = {
  children: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  onReset?: () => void;
  right?: ReactNode;
  hasAdditionalFilters?: boolean;
  className?: string;
};

export const FilterV2 = ({
  children,
  isOpen,
  onToggle,
  onReset,
  right,
  hasAdditionalFilters = false,
  className,
}: FilterV2Props) => {
  const hasRight = right != null && right !== false;

  return (
    <div className={cn("flex min-w-0 flex-row flex-wrap items-end justify-end gap-2", className)}>
      {children}
      <div className="flex flex-row flex-wrap items-end gap-2">
        {hasAdditionalFilters && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onToggle}
            className={cn(
              "h-[2.4rem] w-[2.4rem] shrink-0",
              isOpen && "border-blue-600 text-blue-600",
            )}
          >
            <ListFilterPlus className="h-5 w-5" />
          </Button>
        )}
        {onReset && <ResetButton onReset={onReset} />}
      </div>
      {hasRight && (
        <div className="ml-auto flex shrink-0 flex-row flex-wrap items-center gap-2">{right}</div>
      )}
    </div>
  );
};

type AdditionalFilterV2Props = {
  children: ReactNode;
  isOpen: boolean;
  className?: string;
};

export const AdditionalFilterV2 = ({ children, isOpen, className }: AdditionalFilterV2Props) => (
  <div
    className={cn(
      "grid transition-[grid-template-rows] duration-300 ease-in-out [contain:inline-size]",
      className,
    )}
    style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
  >
    <div className="overflow-hidden pt-4">
      <div className="rounded-md border border-slate-200 bg-white p-4">{children}</div>
    </div>
  </div>
);
